import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MapPin, Plus, X, Calculator, Loader2, Navigation, ArrowRightLeft, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const LocationInput = ({ value, onChange, placeholder, testId, onRemove }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const containerRef = useRef(null);

  const closeDropdown = useCallback(() => {
    clearTimeout(debounceRef.current);
    requestIdRef.current += 1;
    setOpen(false);
  }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closeDropdown();
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeDropdown();
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeDropdown]);

  const fetchSuggestions = useCallback((q) => {
    clearTimeout(debounceRef.current);
    if (!q || q.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const rid = ++requestIdRef.current;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/geocode-suggest`, { params: { q } });
        if (rid !== requestIdRef.current) return;
        setSuggestions(res.data);
        setOpen(res.data.length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 400);
  }, []);

  return (
    <div className="relative flex-1" ref={containerRef}>
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-[#d4af37] flex-shrink-0" />
        <Input
          data-testid={testId}
          value={value.name}
          placeholder={placeholder}
          onChange={(e) => {
            onChange({ name: e.target.value, lat: null, lon: null, state: null });
            fetchSuggestions(e.target.value);
          }}
          onBlur={() => setTimeout(() => closeDropdown(), 200)}
          className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#d4af37]"
        />
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            data-testid={`${testId}-remove`}
            className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
            aria-label="Remove stop"
          >
            <X className="w-4 h-4" />
          </button>
        ) : null}
      </div>
      {open && suggestions.length > 0 ? (
        <div
          data-testid={`${testId}-suggestions`}
          className="absolute z-20 mt-1 left-6 right-0 bg-[#1a1a1a] border border-white/10 rounded-md shadow-lg max-h-52 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <button
              key={`${s.lat}-${s.lon}`}
              type="button"
              data-testid={`${testId}-suggestion-${i}`}
              className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-[#d4af37]/10 border-b border-white/5 last:border-0"
              onMouseDown={() => {
                onChange({ name: s.name.split(',').slice(0, 2).join(','), lat: s.lat, lon: s.lon, state: s.state });
                closeDropdown();
              }}
            >
              {s.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const FareCalculator = () => {
  const [stops, setStops] = useState([
    { name: '', lat: null, lon: null, state: null },
    { name: '', lat: null, lon: null, state: null },
  ]);
  const [vehicleType, setVehicleType] = useState('sedan');
  const [tripType, setTripType] = useState('oneway');
  const [days, setDays] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const updateStop = (index) => (val) => {
    setStops((prev) => prev.map((s, i) => (i === index ? val : s)));
  };

  const addStop = () => {
    setStops((prev) => [...prev.slice(0, -1), { name: '', lat: null, lon: null, state: null }, prev[prev.length - 1]]);
  };

  const removeStop = (index) => {
    setStops((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCalculate = async () => {
    if (stops.filter((s) => s.name.trim()).length < 2) {
      toast.error('Please enter at least a source and destination');
      return;
    }
    if (!customerName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    const phoneDigits = customerPhone.replace(/\D/g, '');
    const normalized = phoneDigits.length === 12 && phoneDigits.startsWith('91') ? phoneDigits.slice(2) : phoneDigits;
    if (normalized.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/fare-estimate`, {
        locations: stops,
        vehicleType,
        tripType,
        days: tripType === 'roundtrip' && Number(days) >= 1 ? Math.floor(Number(days)) : null,
        name: customerName.trim(),
        phone: customerPhone.trim(),
      });
      setResult(res.data);
      toast.success('Estimate ready! Our team has been notified and will contact you shortly.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not calculate fare. Please check the locations.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-16 max-w-4xl mx-auto scroll-mt-28" id="fare-calculator">
      <Card className="lux-card rounded-2xl shadow-none overflow-hidden" data-testid="fare-calculator-card">
        <div className="bg-[#121212] border-b border-[#d4af37]/20 px-6 py-5 sm:px-8">
          <h3 className="font-display text-xl sm:text-2xl font-semibold text-white flex items-center">
            <Calculator className="w-6 h-6 mr-3 text-[#d4af37]" />
            Fare Calculator
          </h3>
          <p className="text-sm text-gray-300 mt-1">
            Enter your route and get an instant outstation fare estimate
          </p>
        </div>
        <CardContent className="p-6 sm:p-8 space-y-5">
          {/* Stops */}
          <div className="space-y-5">
            {stops.map((stop, index) => {
              const isFirst = index === 0;
              const isLast = index === stops.length - 1;
              const label = isFirst ? 'Source' : isLast ? 'Destination' : `Stop ${index}`;
              return (
                <div key={`stop-${index}`}>
                  <Label className="text-xs font-semibold text-gray-400 mb-1.5 block ml-6">{label}</Label>
                  <LocationInput
                    value={stop}
                    onChange={updateStop(index)}
                    placeholder={isFirst ? 'e.g. Bangalore' : isLast ? 'e.g. Mysuru' : 'e.g. Ramanagara'}
                    testId={isFirst ? 'fare-calc-source' : isLast ? 'fare-calc-destination' : `fare-calc-stop-${index}`}
                    onRemove={!isFirst && !isLast ? () => removeStop(index) : null}
                  />
                </div>
              );
            })}
            <button
              type="button"
              onClick={addStop}
              data-testid="fare-calc-add-stop"
              className="ml-6 inline-flex items-center text-sm font-medium text-[#d4af37] hover:text-[#c9a35c]"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Stop
            </button>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Vehicle</Label>
              <select
                data-testid="fare-calc-vehicle"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                className="w-full border border-white/10 rounded-md px-3 py-2 text-sm bg-[#1a1a1a] text-white"
              >
                <option value="sedan">Sedan — ₹14/km</option>
                <option value="suv">SUV — ₹18/km</option>
                <option value="tempo">Tempo Traveller — ₹24/km</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Trip Type</Label>
              <div className="flex rounded-full border border-white/10 overflow-hidden">
                <button
                  type="button"
                  data-testid="fare-calc-oneway"
                  onClick={() => setTripType('oneway')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors ${
                    tripType === 'oneway' ? 'bg-[#d4af37] text-black' : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#222]'
                  }`}
                >
                  <ArrowRight className="w-4 h-4" /> One Way
                </button>
                <button
                  type="button"
                  data-testid="fare-calc-roundtrip"
                  onClick={() => setTripType('roundtrip')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors ${
                    tripType === 'roundtrip' ? 'bg-[#d4af37] text-black' : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#222]'
                  }`}
                >
                  <ArrowRightLeft className="w-4 h-4" /> Round Trip
                </button>
              </div>
            </div>
          </div>

          {tripType === 'roundtrip' ? (
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Number of Days (optional)</Label>
              <Input
                data-testid="fare-calc-days"
                type="number"
                min="1"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                placeholder="e.g. 3 (auto-calculated if left blank)"
                className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#d4af37]"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Minimum 300 km/day billing applies on round trips.
              </p>
            </div>
          ) : null}

          {/* Contact details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Your Name *</Label>
              <Input
                data-testid="fare-calc-name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="e.g. Rahul Sharma"
                className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#d4af37]"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Phone Number *</Label>
              <Input
                data-testid="fare-calc-phone"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="e.g. 9876543210"
                inputMode="tel"
                className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#d4af37]"
              />
            </div>
          </div>

          <Button
            onClick={handleCalculate}
            disabled={loading}
            data-testid="fare-calc-submit"
            className="w-full rounded-full bg-[#d4af37] hover:bg-[#c9a35c] text-black font-semibold py-6 text-base transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Calculating…
              </>
            ) : (
              <>
                <Navigation className="w-5 h-5 mr-2" /> Calculate Fare
              </>
            )}
          </Button>

          {/* Result */}
          {result ? (
            <div className="rounded-xl border border-[#d4af37]/30 overflow-hidden" data-testid="fare-calc-result">
              <div className="bg-[#d4af37]/10 px-4 py-3 border-b border-[#d4af37]/20">
                <p className="text-sm font-semibold text-white">
                  {result.route.join(' → ')}
                  {result.trip_type === 'roundtrip' ? ' (Round Trip)' : ''}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Total distance: <span className="font-semibold">{result.total_km} km</span>
                  {result.trip_type === 'roundtrip' ? ` (${result.one_way_km} km one way)` : ''}
                  {' · '}{result.days} day(s)
                  {result.min_km_applied ? (
                    <span> · billed at <span className="font-semibold">{result.billable_km} km</span> (min 300 km/day)</span>
                  ) : ''}
                </p>
                {result.days_raised ? (
                  <p className="text-xs text-[#d4af37] mt-1 font-medium" data-testid="fare-calc-days-raised-note">
                    Days adjusted to {result.days} — this route needs at least {result.days} day(s) at 300 km/day.
                  </p>
                ) : null}
              </div>
              <div className="px-4 py-3 space-y-2 text-sm bg-[#121212]">
                <div className="flex justify-between">
                  <span className="text-gray-400">Base Fare ({result.billable_km} km × ₹{result.per_km_rate}/km)</span>
                  <span className="font-medium text-gray-200">{formatINR(result.base_fare)}</span>
                </div>
                {result.driver_allowance > 0 ? (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Driver Allowance ({result.days} day(s) × ₹400)</span>
                    <span className="font-medium text-gray-200">{formatINR(result.driver_allowance)}</span>
                  </div>
                ) : null}
                {result.one_way_drop_charge > 0 ? (
                  <div className="flex justify-between">
                    <span className="text-gray-400">One Way Drop Charges ({result.total_km} km × ₹{result.per_km_rate / 2}/km)</span>
                    <span className="font-medium text-gray-200" data-testid="fare-calc-drop-charge">{formatINR(result.one_way_drop_charge)}</span>
                  </div>
                ) : null}
                {result.state_permit > 0 ? (
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      State Permit <span className="text-xs text-gray-500">(interstate: {result.states.join(' ↔ ')})</span>
                    </span>
                    <span className="font-medium text-gray-200">{formatINR(result.state_permit)}</span>
                  </div>
                ) : null}
              </div>
              <div className="bg-black px-4 py-3 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-200">Estimated Total</span>
                <span className="text-2xl font-extrabold text-[#d4af37]" data-testid="fare-calc-total">
                  {formatINR(result.total_estimate)}
                </span>
              </div>
              <div className="bg-[#121212] border-t border-white/5 px-4 py-2.5 space-y-1">
                {result.state_permit > 0 ? (
                  <p className="text-xs text-gray-400">
                    * State permit added for interstate trip — charges may vary basis the state.
                  </p>
                ) : (
                  <p className="text-xs text-gray-400">
                    * No state permit — trip is within {result.states[0] || 'the same state'}.
                  </p>
                )}
                <p className="text-xs text-gray-400">
                  * Toll & Parking charges are NOT included and will be charged separately as per actuals. Final fare may vary slightly based on actual route.
                </p>
              </div>
              <div className="p-4 bg-[#121212] border-t border-white/10">
                <a href="#booking">
                  <Button
                    data-testid="fare-calc-book-btn"
                    className="w-full rounded-full bg-[#d4af37] hover:bg-[#c9a35c] text-black font-semibold transition-colors"
                  >
                    Book This Trip
                  </Button>
                </a>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
